import { Checkbox, FormControlLabel, Switch } from '@mui/material';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';

import { useI18n } from '../../../../../../../../application/language/useI18n';
import { FormModalContainer } from '../../../../../../../design/dataTable/form/view/FormModalContainer';
import { FormActions } from '../../../../../../../design/form/FormActions';
import { FormAutocomplete } from '../../../../../../../design/form/FormAutocomplete';
import { FormTextField } from '../../../../../../../design/form/FormTextField';
import { UserModalDomain } from './UserModalDomain';

export interface IUserEntityAdminTableProperties {
  domain: UserModalDomain;
}

const adminUserLogin = 'admin';

export const UserModal = observer(({ domain }: IUserEntityAdminTableProperties) => {
  const { translate } = useI18n();

  const isSSOUser = domain.ui.model.entity.sourceType === 'sso' && !!domain.ui.model.entity.id;

  useEffect(() => {
    domain.layoutDomain.setPageTitle(
      domain.ui.model.entity.id ? translate('phrases.editUser') : translate('phrases.addUser'),
    );
  }, [domain]);

  return (
    <FormModalContainer onClose={domain.onCancelUpdates} title={domain.layoutDomain.ui.pageTitle.value}>
      {!isSSOUser && (
        <>
          <UserNameCustomField domain={domain} />
          <UserEmailCustomField domain={domain} />
          <UserLoginCustomField domain={domain} />
          <UserPasswordCustomField domain={domain} />
          <UserConfirmPasswordCustomField domain={domain} />
          <UserMustChangePasswordCheckbox domain={domain} />
          <UserActiveStatusCustomSwitch domain={domain} />
        </>
      )}

      <UserRoleTypeDropDown domain={domain} />

      <FormActions onSave={() => domain.onSaveUpdates()} onCancel={() => domain.onCancelUpdates()} isEdit={!!domain.ui.model.entity.id} />
    </FormModalContainer>
  );
});

const UserNameCustomField = observer(({ domain }: { domain: UserModalDomain }) => {
  const { translate } = useI18n();
  return (
    <FormTextField
      required={true}
      label={translate('settingsUser.fio')}
      value={domain.ui.model.entity.displayName}
      onChange={domain.getUpdateFieldHandler('displayName')}
      errorMessage={domain.getValidationErrorFor('displayName')?.message}
      disabled={domain.ui.model.entity.login === 'admin' && !!domain.ui.model.entity.id}
    />
  );
});

const UserEmailCustomField = observer(({ domain }: { domain: UserModalDomain }) => {
  const { translate } = useI18n();

  return (
    <FormTextField
      errorMessage={domain.getValidationErrorFor('email')?.message}
      value={domain.ui.model.entity.email}
      onChange={domain.getUpdateFieldHandler('email')}
      label={translate('settingsUser.email')}
      required={true}
      disabled={domain.ui.model.entity.login === adminUserLogin && !!domain.ui.model.entity.id}
    />
  );
});
const UserLoginCustomField = observer(({ domain }: { domain: UserModalDomain }) => {
  const { translate } = useI18n();

  return (
    <FormTextField
      errorMessage={domain.getValidationErrorFor('login')?.message}
      value={domain.ui.model.entity.login}
      onChange={domain.getUpdateFieldHandler('login')}
      label={translate('settingsUser.login')}
      required={true}
      disabled={domain.ui.model.entity.login === adminUserLogin && !!domain.ui.model.entity.id}
    />
  );
});
const UserPasswordCustomField = observer(({ domain }: { domain: UserModalDomain }) => {
  const { translate } = useI18n();

  return (
    <FormTextField
      type="password"
      errorMessage={domain.getValidationErrorFor('password')?.message}
      value={domain.ui.model.entity.password}
      onChange={domain.getUpdateFieldHandler('password')}
      label={translate('phrases.password')}
      required={true}
    />
  );
});
const UserConfirmPasswordCustomField = observer(({ domain }: { domain: UserModalDomain }) => {
  const { translate } = useI18n();

  return (
    <FormTextField
      type="password"
      errorMessage={domain.getValidationErrorFor('confirmPassword')?.message}
      value={domain.ui.model.entity.confirmPassword}
      onChange={domain.getUpdateFieldHandler('confirmPassword')}
      label={translate('phrases.confirmPassword')}
      required={true}
    />
  );
});

const UserMustChangePasswordCheckbox = observer(({ domain }: { domain: UserModalDomain }) => {
  const { translate } = useI18n();

  const [checked, setChecked] = useState(domain.ui.model.entity.directives?.mustChangePassword || false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setChecked(newValue);
    domain.ui.model.entity.directives = { mustChangePassword: newValue };
  };

  useEffect(() => {
    if (domain.ui.model.entity.directives?.mustChangePassword === undefined) {
      setChecked(domain.ui.model.entity.directives?.mustChangePassword || true);
      domain.ui.model.entity.directives = { mustChangePassword: true };
    }
  }, [domain.ui.model.entity.directives?.mustChangePassword]);

  return (
    <FormControlLabel
      style={{ width: '100%', padding: '16px', margin: 0 }}
      control={<Checkbox color="primary" checked={checked} onChange={handleChange} />}
      label={translate('phrases.mustChangePasswordAfterLogin')}
    />
  );
});

const UserRoleTypeDropDown = observer(({ domain }: { domain: UserModalDomain }) => {
  const { translate } = useI18n();

  const roleTypeValues = [
    ...domain.ui.systemRoleId.list.map((role) => ({
      value: role.id,
      name: role.name,
    })),
  ];
  return (
    <FormAutocomplete
      values={roleTypeValues}
      onChangeSelect={(newValue: any) => {
        domain.ui.model.entity.systemRoleId = newValue.value;
      }}
      selected={domain.ui.model.entity.systemRoleId}
      label={translate('settingsUser.systemRole')}
      errorMessage={domain.getValidationErrorFor('systemRoleId')?.message}
      disabled={domain.ui.model.entity.login === adminUserLogin && !!domain.ui.model.entity.id}
    />
  );
});

const UserActiveStatusCustomSwitch = observer(({ domain }: { domain: UserModalDomain }) => {
  const { translate } = useI18n();

  const [isChecked, setChecked] = useState(domain.ui.model.entity.isActive);

  const handleChange = (event: any) => {
    setChecked(event.target.checked);
    domain.ui.model.entity.isActive = event.target.checked;
  };
  useEffect(() => {
    setChecked(domain.ui.model.entity.isActive);
  }, [domain.ui.model.entity.isActive]);
  return (
    <FormControlLabel
      style={{ width: '100%', padding: '16px', margin: 0 }}
      control={<Switch color={'success'} checked={isChecked} onChange={handleChange} defaultChecked={true} />}
      label={translate('settingsUser.status')}
      disabled={domain.ui.model.entity.login === adminUserLogin && !!domain.ui.model.entity.id}
    />
  );
});
